import { Text, Tooltip, VStack, WarningSeverity, WarningSeverityIcon, type ISubaccount } from '@talos/kyoko';
import { compact, sortBy } from 'lodash-es';
import { useMemo } from 'react';
import { useSubAccountRollupMemberships, useSubAccounts } from '../../../../providers';

/**
 * This component will conditionally render a warning icon with a tooltip warning the user if the
 * sub account <-> rollup hierarchy the user has set up is _not_ a tree. If it is a tree, nothing is rendered.
 */
export const SubAccountTreeWarning = () => {
  const { isRollupHierarchyTree, subAccountIDsAssignedToMoreThanOneRollup } = useSubAccountRollupMemberships();
  const { subAccountsByID } = useSubAccounts();

  const subAccsInViolationOfTree = useMemo(() => {
    return sortBy(
      compact(subAccountIDsAssignedToMoreThanOneRollup.map(id => subAccountsByID.get(id))),
      'DisplayName' satisfies keyof ISubaccount
    );
  }, [subAccountIDsAssignedToMoreThanOneRollup, subAccountsByID]);

  const subAccsInViolationText = useMemo(() => {
    // Show the first five. If there are more than five, suffix with a (and n more) text
    const nShown = 5;
    const firstNText = subAccsInViolationOfTree
      .slice(0, nShown)
      .map(sa => sa.DisplayName)
      .join(', ');
    return `${firstNText}${
      subAccsInViolationOfTree.length > nShown ? ` (and ${subAccsInViolationOfTree.length - nShown} more)` : ''
    }.`;
  }, [subAccsInViolationOfTree]);

  if (isRollupHierarchyTree) {
    // all good, dont show the warning
    return <></>;
  }

  return (
    <Tooltip
      tooltip={
        <VStack gap="spacing16" alignItems="flex-start" data-testid="sub-accounts-not-a-tree-warning-text">
          <Text>
            Your Rollup hierarchy is not in a tree structure because some Sub Accounts are assigned to multiple Rollups.
            These Sub Accounts will appear in only one Rollup and will be excluded from the others, resulting in
            incomplete aggregations for those Rollups.
          </Text>

          <Text>Sub Account(s) in question: {subAccsInViolationText}</Text>
        </VStack>
      }
    >
      <WarningSeverityIcon severity={WarningSeverity.MEDIUM} data-testid="sub-accounts-not-a-tree-warning" />
    </Tooltip>
  );
};
